import { Group } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import type {
  MRT_ColumnDef,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_VisibilityState
} from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useMemo } from "react";
import { IconButton } from "../../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { ColumnVisibilityMenu } from "../../../../mantine/mrt/components/header/ColumnVisibilityMenu";
import { ScrollControls } from "../../../../mantine/mrt/components/toolbar/ScrollControls";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { EnergyRevenuesCellCheckStateColumn } from "../../../../types/api.types";
import { EnergyRevenuesCellCheckStateState } from "../../../../types/api.types";
import { downloadAsExcel } from "../../../../utils/files/downloadAsExcel";
import { NoDataMessage } from "../../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { useEnergyRevenuesPageParams } from "../../hooks/useEnergyRevenuesPageParams";
import { useEnergyRevenuesVerificationMutations } from "../../hooks/useEnergyRevenuesVerificationMutations";
import type {
  EnergyPriceUnits,
  EnergyRevenuesCellChecksResponse,
  EnergyRevenuesListItem,
  EnergyRevenuesListResponse
} from "../../RevenueMonitoring.types";
import { getExportFileDatesPostfix } from "../utils/getExportFileDatesPostfix";
import { prepareTableDataForExport } from "../utils/prepareTableDataForExport";
import { getAusfallarbeitColumnGroups } from "./columnGroups/getAusfallarbeitColumnGroups";
import { getDirectMarketingColumnGroup } from "./columnGroups/getDirectMarketingColumnGroup";
import { getMarketPremiumColumnGroup } from "./columnGroups/getMarketPremiumColumnGroup";
import { getMarktwertOverviewColumnGroup } from "./columnGroups/getMarktwertOverviewColumnGroup";
import { getPeriodStartColumn } from "./columns/getPeriodStartColumn";
import { getRevenuePerKwhColumn } from "./columns/getRevenuePerKwhColumn";
import {
  AUSFALLARBEIT_COLUMN_GROUPS,
  AUSFALLARBEIT_COLUMN_KEYS,
  BASE_COLUMN_GROUPS,
  BASE_COLUMN_KEYS
} from "./EnergyRevenuesTable.constants";
import "./EnergyRevenuesTable.scss";
import { convertMarktwertValuesForDisplayUnit } from "./utils/convertEnergyRevenuesData";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "period_start",
    desc: true
  }
];

export const REVENUE_TABLE_HEADER_TEXTS = {
  period_start: "Zeitraum (alle Angaben ohne MwSt.)",
  vermarktung_volume: "Menge Vermarktung (kWh)",
  vermarktung_net_revenue: "Erlös Vermarktung (EUR)",
  vermarktung_fee: "Vermarktungs­entgelt (EUR)", // here is a unicode soft-hypen
  net_revenue_minus_fee: "Vermarktung abzgl. Entgelt (EUR)",
  market_premium_volume: "Menge Marktprämie (kWh)",
  market_premium_revenue: "Erlös Marktprämie (EUR)",
  negative_price_volume: "§ 51 EEG Menge mit negativem Preis (kWh)",
  negative_price_revenue: "Entgangene Marktprämie für § 51 EEG Menge (EUR)",
  ausfallarbeit_redispatch: "Menge Ausfallarbeit Redispatch (kWh)",
  revenue_redispatch: "Vermarktungsvergütung Redispatch (EUR)",
  redispatch_entgelt: "Entgelt (Redispatch) (EUR)",
  aufwandsersatz_redispatch: "Aufwandsersatz Redispatch (EUR)",
  ausfallarbeit_foerderung_redispatch: "Menge Förderung (Redispatch) (kWh)",
  revenue_foerderung_redispatch: "Erlöse Förderung (Redispatch) (EUR)",
  ausfallarbeit_dva: "Menge Vermarktung (Abschaltung) (kWh)",
  revenue_dva: "Erlöse Vermarktung (Abschaltung) (EUR)",
  dva_entgelt: "Entgelt (Abschaltung) (EUR)",
  revenue_per_kwh: "∅ Erlös (ohne Entgelte) {ENERGY_PRICE_UNIT}",
  monatsmarktwert_solar: "MW Solar {ENERGY_PRICE_UNIT} aus Netztransparenz",
  monatsmarktwert_wind: "MW Wind {ENERGY_PRICE_UNIT} aus Netztransparenz",
  jahresmarktwert_solar: "JW Solar {ENERGY_PRICE_UNIT} aus Netztransparenz",
  jahresmarktwert_wind: "JW Wind {ENERGY_PRICE_UNIT} aus Netztransparenz"
};

export function getEnergyUnitHeaderText(
  headerKey:
    | "revenue_per_kwh"
    | "monatsmarktwert_solar"
    | "monatsmarktwert_wind"
    | "jahresmarktwert_solar"
    | "jahresmarktwert_wind",
  energyPriceUnit: EnergyPriceUnits
): string {
  const unitPattern = /\{ENERGY_PRICE_UNIT\}/;

  return REVENUE_TABLE_HEADER_TEXTS[headerKey].replace(
    unitPattern,
    `(${energyPriceUnit})`
  );
}

interface EnergyRevenuesTableProps {
  energyRevenuesData: EnergyRevenuesListResponse;
  dataVerificationColumns?: Array<EnergyRevenuesCellCheckStateColumn>;
  dataVerificationItems?: EnergyRevenuesCellChecksResponse;
  energyPriceUnit?: EnergyPriceUnits;
  exportFileName: string;
  isLoading?: boolean;
  companyId?: number;
  toolbarComponents?: React.ReactNode;
}

function EnergyRevenuesTable({
  energyRevenuesData,
  dataVerificationColumns = [],
  dataVerificationItems = [],
  energyPriceUnit = "ct/kWh",
  exportFileName,
  isLoading,
  companyId,
  toolbarComponents
}: EnergyRevenuesTableProps) {
  const { showErrorToast } = useToast();
  const {
    sort,
    setSort,
    filters,
    setFilters,
    columnVisibility: rawColumnVisibility,
    setColumnVisibility
  } = useEnergyRevenuesPageParams();

  const convertedEnergyRevenuesData = useMemo(
    () =>
      convertMarktwertValuesForDisplayUnit(energyRevenuesData, energyPriceUnit),
    [energyRevenuesData, energyPriceUnit]
  );

  const applicableMarktwertColumns = useMemo(
    () =>
      BASE_COLUMN_GROUPS.marktwert_overview.children.filter((col) =>
        convertedEnergyRevenuesData.some((row) => Object.hasOwn(row, col))
      ),
    [convertedEnergyRevenuesData]
  );

  const columnGroups = useMemo(() => {
    const groups: Record<
      string,
      { label: string; children: ReadonlyArray<string> }
    > = {
      direct_marketing: BASE_COLUMN_GROUPS.direct_marketing
    };

    if (applicableMarktwertColumns.length > 0) {
      groups.marktwert_overview = {
        label: BASE_COLUMN_GROUPS.marktwert_overview.label,
        children: applicableMarktwertColumns
      };
    }

    groups.market_premium = BASE_COLUMN_GROUPS.market_premium;

    Object.assign(groups, AUSFALLARBEIT_COLUMN_GROUPS);

    return groups;
  }, [applicableMarktwertColumns]);

  const columnVisibility = useMemo(() => {
    const defaultHiddenGroups = ["marktwert_overview"];
    const visibility = { ...rawColumnVisibility };

    Object.entries(columnGroups).forEach(([groupId, group]) => {
      if (!(groupId in visibility)) {
        if (defaultHiddenGroups.includes(groupId)) {
          visibility[groupId] = false;
          group.children.forEach((col: string) => {
            if (!(col in visibility)) {
              visibility[col] = false;
            }
          });
        } else {
          visibility[groupId] = group.children.some(
            (col: string) => visibility[col] !== false
          );
        }
      }
    });

    return visibility;
  }, [rawColumnVisibility, columnGroups]);
  const { createVerificationMutation } =
    useEnergyRevenuesVerificationMutations(companyId);

  type VisibilityUpdater =
    MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState);

  const handleColumnVisibilityChange = useCallback(
    (updater: VisibilityUpdater) => {
      const currentVisibility = columnVisibility;
      const newVisibility =
        typeof updater === "function" ? updater(currentVisibility) : updater;

      const finalVisibility = { ...currentVisibility, ...newVisibility };

      Object.entries(columnGroups).forEach(([groupId, group]) => {
        if (
          groupId in newVisibility &&
          newVisibility[groupId] !== currentVisibility[groupId]
        ) {
          const isVisible = newVisibility[groupId];
          finalVisibility[groupId] = isVisible;
          group.children.forEach((col: string) => {
            finalVisibility[col] = isVisible;
          });
        }
      });

      setColumnVisibility(finalVisibility);
    },
    [columnVisibility, setColumnVisibility, columnGroups]
  );

  const handleBatchCreateVerification = useCallback(
    async (energyRevenuesListItem: EnergyRevenuesListItem) => {
      if (!companyId) return;
      const period_start = energyRevenuesListItem.period_start;
      const fieldsWithoutVerification = dataVerificationColumns.filter(
        (column) =>
          !dataVerificationItems.some(
            (item) =>
              item.column === column && item.period_start === period_start
          )
      );
      try {
        await Promise.all(
          Object.values(fieldsWithoutVerification).map((column) =>
            createVerificationMutation.mutateAsync(
              {
                person_id: companyId,
                period_start,
                column,
                state: EnergyRevenuesCellCheckStateState.inProgress,
                calculated_amount: Number(energyRevenuesListItem[column])
              },
              {
                onError: (error) => {
                  showErrorToast(`${period_start} ${column}: ${error.message}`);
                }
              }
            )
          )
        );
      } catch (error) {
        showErrorToast(error);
      }
    },
    [
      companyId,
      createVerificationMutation,
      dataVerificationItems,
      dataVerificationColumns,
      showErrorToast
    ]
  );

  const columns = useMemo<Array<MRT_ColumnDef<EnergyRevenuesListItem>>>(
    () => [
      {
        id: "period_group",
        header: "",
        mantineTableHeadCellProps: {
          // @ts-expect-error group in initial state columnPinning doesnt work, add data-attr manually
          "data-column-pinned": "left",
          "data-last-left-pinned": true
        },
        Header: ({ table }) => (
          <ColumnVisibilityMenu
            columnGroups={columnGroups}
            columnVisibility={columnVisibility}
            table={table}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />
        ),
        columns: [
          getPeriodStartColumn(
            handleBatchCreateVerification,
            dataVerificationColumns.length > 0
          )
        ]
      },
      getDirectMarketingColumnGroup({
        companyId,
        dataVerificationColumns,
        dataVerificationItems,
        columnVisibility
      }),
      ...(applicableMarktwertColumns.length > 0
        ? [
            getMarktwertOverviewColumnGroup({
              columnVisibility,
              applicableColumns: applicableMarktwertColumns,
              energyPriceUnit
            })
          ]
        : []),
      getMarketPremiumColumnGroup({
        companyId,
        dataVerificationColumns,
        dataVerificationItems,
        columnVisibility
      }),
      ...getAusfallarbeitColumnGroups({
        companyId,
        dataVerificationColumns,
        dataVerificationItems,
        columnVisibility
      }),
      getRevenuePerKwhColumn(energyPriceUnit)
    ],
    [
      companyId,
      dataVerificationColumns,
      dataVerificationItems,
      energyPriceUnit,
      handleBatchCreateVerification,
      applicableMarktwertColumns,
      columnVisibility,
      handleColumnVisibilityChange,
      columnGroups
    ]
  );

  const columnOrder = useMemo(
    () => [
      "period_start",
      ...BASE_COLUMN_KEYS,
      ...AUSFALLARBEIT_COLUMN_KEYS,
      "revenue_per_kwh"
    ],
    []
  );

  const tableOptions = {
    ...getDefaultMRTOptions<EnergyRevenuesListItem>({
      hideBorder: true,
      className: "EnergyRevenuesTable"
    }),
    enableColumnActions: true,
    enableColumnResizing: true,
    enableHiding: true,
    enableFacetedValues: true,
    enableColumnPinning: true,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 15 },
      columnOrder,
      // period_group here doesnt work, because it is a group, not a column - so we add data-attr manually above
      columnPinning: { left: ["period_start", "period_group"] }
    },
    state: {
      sorting: sort?.length ? sort : DEFAULT_SORTED,
      columnFilters: filters,
      isLoading,
      columnVisibility,
      columnOrder
    },
    renderEmptyRowsFallback: () => (
      <NoDataMessage
        message={
          energyRevenuesData && energyRevenuesData.length > 0
            ? "Keine Einträge gefunden."
            : "Es sind noch keine Daten verfügbar."
        }
      />
    ),
    onSortingChange: (newSorting) => {
      const sortingValue =
        typeof newSorting === "function" ? newSorting(sort) : newSorting;
      setSort(sortingValue);
    },
    onColumnFiltersChange: (newFilters) => {
      const filtersValue =
        typeof newFilters === "function" ? newFilters(filters) : newFilters;
      setFilters(filtersValue);
    },
    onColumnVisibilityChange: handleColumnVisibilityChange,
    mantineColumnActionsButtonProps: ({ column }) => ({
      size: "xs" as const,
      "data-active":
        column.getIsSorted() || column.getIsFiltered() ? true : undefined
    }),
    renderBottomToolbarCustomActions: ({ table }) => (
      <ScrollControls table={table} />
    ),
    renderTopToolbarCustomActions: () => (
      <Group
        align="center"
        justify={toolbarComponents ? "space-between" : "flex-end"}
        p={0}
        w="stretch"
      >
        {toolbarComponents}
        <IconButton
          disabled={!energyRevenuesData.length}
          icon={IconDownload}
          variant="filled"
          onClick={handleRevenueTableExport}
        >
          Exportieren
        </IconButton>
      </Group>
    )
  } satisfies Partial<MRT_TableOptions<EnergyRevenuesListItem>>;

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: convertedEnergyRevenuesData
  });

  function handleRevenueTableExport() {
    const tableData = prepareTableDataForExport(table);
    const datesPostfix = getExportFileDatesPostfix(tableData);

    downloadAsExcel(tableData, `${exportFileName}-${datesPostfix}`);
  }

  return <MantineReactTable table={table} />;
}

export { EnergyRevenuesTable, EnergyRevenuesTableProps };
